import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash } from '@phosphor-icons/react';
import { api } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';

export const CategoryManager = ({ open, onOpenChange, categories, onChanged }) => {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post('/admin/posts/categories', { label: label.trim() });
      setLabel('');
      toast.success('Category added');
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add category');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (category) => {
    try {
      await api.delete(`/admin/posts/categories/${category.id}`);
      toast.success(`${category.label} removed`);
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove category');
    }
  };

  const rename = async (category, nextLabel) => {
    if (!nextLabel.trim() || nextLabel === category.label) return;
    try {
      await api.put(`/admin/posts/categories/${category.id}`, { label: nextLabel.trim() });
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not rename category');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="category-manager-dialog">
        <DialogHeader>
          <DialogTitle>Categories</DialogTitle>
          <DialogDescription>
            These are the filter pills on the public News &amp; Stories page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center gap-2" data-testid={`category-row-${category.slug}`}>
              <Input
                defaultValue={category.label}
                onBlur={(e) => rename(category, e.target.value)}
                className="h-9"
                data-testid={`category-label-${category.slug}`}
              />
              <span className="text-xs text-foreground-muted w-20 shrink-0">
                {category.post_count} post{category.post_count === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={() => remove(category)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                title="Delete category"
                data-testid={`category-delete-${category.slug}`}
              >
                <Trash size={15} />
              </button>
            </div>
          ))}
        </div>

        <form onSubmit={create} className="flex items-center gap-2 pt-4 border-t border-black/[0.06]">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="New category name"
            className="h-9"
            data-testid="new-category-input"
          />
          <Button
            type="submit"
            disabled={busy}
            className="h-9 rounded-full px-4 bg-primary hover:bg-primary-hover"
            data-testid="add-category-button"
          >
            <Plus size={15} className="mr-1.5" /> Add
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
